import { Component, HostBinding, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subscription, map, debounceTime, distinctUntilChanged } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
	FieldExpression,
	LuceneExpression,
	NodeExpression,
	term,
	parse as luceneToParse,
	toString as luceneToString
} from 'lucene';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { Order } from '../../models/order';
import { OrderService } from '../../services/order.service';
import { UNDERSCORE } from '../../../shared/components/utils/constants';
import { OrderByDirection, OrderBy } from '../../../shared/utils/constant';
import { ORDER, PRODUCT, CDD } from '../../../shared/models/settings';
import { FavoriteOrders } from '../../models/favorite-orders';
import { ListFilterService } from '../../../shared/services/list-filter.service';
import { ListFilter } from '../../../shared/models/filter';
import { DatacenterType } from '../../../shared/models/datacenter-aggrid-view';
import { DimensionPipe } from '../../../shared/pipes/format-dimension.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';

interface OrderExtended extends Order {
	isFavoriteDisabled?: boolean;
}

@Component({
	selector: 'copo-order-list',
	templateUrl: './order-list.component.html',
	styleUrl: './order-list.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule,
		VirtualScrollerDirective,
		NgFor,
		RouterLink,
		LoadingSpinnerComponent,
		DatePipe,
		NumberPipe,
		DimensionPipe
	]
})
export class OrderListComponent implements OnInit, OnDestroy {
	@HostBinding('class.copo-order-tracking-block') readonly block = true;

	readonly UNDERSCORE = UNDERSCORE;

	user: User;
	sortWay = OrderByDirection.DESC;
	sortField: 'order' | 'product' | 'cddWeek' = CDD;
	favoriteOrdersSubscription?: Subscription;
	saveFavoriteOrdersSubscription?: Subscription;
	deleteFavoriteOrdersSubscription?: Subscription;
	favoriteOrders: FavoriteOrders[] = [];
	isLoading = true;
	requiredFields: (keyof Order)[] = [
		'supplierOrderNumber',
		'supplierOrderItemNumber',
		'mill',
		'millDescription',
		'thickness',
		'width',
		'length',
		'cddWeek',
		'grossLastBatchInStock',
		'grossFirstBatchInStock'
	];

	searchTextControl = new FormControl('', { nonNullable: true });
	searchTextSubscription?: Subscription;
	filters = '';
	mergedFilter?: LuceneExpression;
	currentFilters?: ListFilter;
	datacenterType: DatacenterType;
	constructor(
		private readonly orderService: OrderService,
		private readonly userService: UserService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly listFilterService: ListFilterService,
	) {
		this.datacenterType = <DatacenterType> this.route.snapshot.data['type'];
		this.currentFilters = this.getAppliedFilters();
		let criterionFilterNode;
		if (this.listFilterService
			.getLuceneFromAllFilters(this.currentFilters)) {
			criterionFilterNode = this.listFilterService.getLuceneFromAllFilters(this.currentFilters);
		}

		if (criterionFilterNode !== undefined) {
			this.mergedFilter = criterionFilterNode;
			this.filters = luceneToString(criterionFilterNode);
		}

		this.user = this.userService.user;
		this.requiredFields.push(
			'grossQtyOrdered',
			'grossQtyInProduction',
			'grossQtyAvailableReadyToShip',
			'grossQtyAvailableInTransit',
			'grossQtyDelivered',
			'steelgradeDescription',
			'customerArticleReference',
			'customerOrderNumber',
			'customerOrderItemNumber'
		);

		const orderTrackingSortList = this.user.getSettings('ordertrackingsortlist');
		if (!orderTrackingSortList) {
			return;
		}
		switch (orderTrackingSortList.value.ordertrackingsortlist) {
		case 'product_desc':
			this.sortWay = OrderByDirection.DESC;
			this.sortField = PRODUCT;
			break;
		case 'product_asc':
			this.sortWay = OrderByDirection.ASC;
			this.sortField = PRODUCT;
			break;
		case 'order_desc':
			this.sortWay = OrderByDirection.DESC;
			this.sortField = ORDER;
			break;
		case 'order_asc':
			this.sortWay = OrderByDirection.ASC;
			this.sortField = ORDER;
			break;
		case 'cdd_desc':
			this.sortWay = OrderByDirection.DESC;
			this.sortField = CDD;
			break;
		case 'cdd_asc':
			this.sortWay = OrderByDirection.ASC;
			this.sortField = CDD;
			break;
		}
	}

	get sortBy() {
		switch (this.sortField) {
		case ORDER:
			return 'customerOrderNumber';
		case PRODUCT:
			return 'steelgrade';
		default:
			return CDD;
		}
	}

	get dataId() {
		return `order-tracking-order-${ this.sortWay
		}-${ this.sortBy
		}-${ this.filters
		}`;
	}

	ngOnInit(): void {
		this.favoriteOrdersSubscription = this.orderService.getFavoriteOrders().subscribe({
			next: favoriteOrders => {
				this.favoriteOrders = favoriteOrders;
				this.isLoading = false;
			},
			error: (error: unknown) => {
				console.error(error);
				this.isLoading = false;
			},
		});

		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.filters = value !== ''
				? this.mergedFilter
					? this.createQuickSearchAndFilterQuery(this.createQuickSearchFilter(value), this.mergedFilter)
					: this.createQuickSearchFilter(value)
				: this.mergedFilter ? luceneToString(this.mergedFilter) : '';
		});
	}

	ngOnDestroy() {
		if (this.favoriteOrdersSubscription) {
			this.favoriteOrdersSubscription.unsubscribe();
		}

		if (this.saveFavoriteOrdersSubscription) {
			this.saveFavoriteOrdersSubscription.unsubscribe();
		}

		if (this.deleteFavoriteOrdersSubscription) {
			this.deleteFavoriteOrdersSubscription.unsubscribe();
		}

		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<Order[]>) => void) {
		provideData(
			this.orderService.getOrders(
				this.requiredFields,
				this.filters,
				index,
				limit,
				new OrderBy(this.sortBy, this.sortWay),
				false
			).pipe(map(res => res.results.map((order: OrderExtended) => {
				const comparedFavoriteNumber = this.favoriteOrders.find(
					favoriteOrder => favoriteOrder.supplierOrderItem === order.supplierOrderItemNumber
						&& favoriteOrder.supplierOrderReference === order.supplierOrderNumber
				);
				order.favorite = comparedFavoriteNumber;
				order.isFavorite = Boolean(comparedFavoriteNumber);
				order.isFavoriteDisabled = false;

				return order;
			})))
		);
	}

	getOrderName(order: Order) {
		return order.customerOrderNumber === ''
			? UNDERSCORE
			: `${ order.customerOrderNumber }/${ order.customerOrderItemNumber }`;
	}

	getQuantity(order: OrderExtended, quantityName: string) {
		const qtyOrdered = order.grossQtyOrdered;
		const qtyInProduction = order.grossQtyInProduction;
		const qtyAvailableReadyToShip = order.grossQtyAvailableReadyToShip;
		const qtyAvailableInTransit = order.grossQtyAvailableInTransit;
		const qtyDelivered = order.grossQtyDelivered;
		switch (quantityName) {
		case 'ordered':
			return qtyOrdered || 0;
		case 'inProduction':
			return qtyInProduction || 0;
		case 'readyToShip':
			return qtyAvailableReadyToShip || 0;
		case 'inTransit':
			return qtyAvailableInTransit || 0;
		default:
			return qtyDelivered || 0;
		}
	}

	deleteFavoriteOrder(order: OrderExtended) {
		if (!order.favorite?.id) {
			return;
		}

		order.isFavoriteDisabled = true;

		this.deleteFavoriteOrdersSubscription = this.orderService.deleteFavoriteOrders(order.favorite.id)
			.subscribe({
				next: () => {
					order.isFavorite = false;
					order.isFavoriteDisabled = false;
				},
				error: (error: unknown) => {
					console.error(error);
					order.isFavoriteDisabled = false;
				}
			});
	}

	saveFavoriteOrder(order: OrderExtended) {
		order.isFavoriteDisabled = true;

		const obj: FavoriteOrders = {
			supplierOrderReference: order.supplierOrderNumber,
			supplierOrderItem: order.supplierOrderItemNumber
		};

		this.saveFavoriteOrdersSubscription = this.orderService.saveFavoriteOrders(obj)
			.subscribe({
				next: res => {
					order.favorite = {
						id: res.id,
						supplierOrderReference: res.supplierOrderReference,
						supplierOrderItem: res.supplierOrderItem
					};

					order.isFavorite = true;
					order.isFavoriteDisabled = false;
				},
				error: (error: unknown) => {
					console.error(error);
					order.isFavoriteDisabled = false;
				}
			});
	}

	createQuickSearchAndFilterQuery(quicksearchQuery: string, filterQuery: LuceneExpression) {
		const globalNode: NodeExpression = {
			left: luceneToParse(quicksearchQuery),
			operator: 'AND',
			right: filterQuery,
			parenthesized: false
		};

		return luceneToString(globalNode);
	}

	createQuickSearchFilter(textSearch: string) {
		const escapedTextFilter = `*${ term.escape(textSearch) }*`;

		const supplierOrderReferenceField: FieldExpression = {
			field: 'supplierOrderNumber',
			term: escapedTextFilter,
			quoted: false
		};

		const customerOrderReferenceField: FieldExpression = {
			field: 'customerOrderNumber',
			term: escapedTextFilter,
			quoted: false
		};

		const customerArticleReferenceField: FieldExpression = {
			field: 'customerArticleReference',
			term: escapedTextFilter,
			quoted: false
		};

		const steelgradeDescriptionField: FieldExpression = {
			field: 'steelgradeDescription',
			term: escapedTextFilter,
			quoted: false
		};

		const supplierCustomerNode = {
			left: supplierOrderReferenceField,
			operator: 'OR',
			right: customerOrderReferenceField,
			parenthesized: false
		};

		const articleSteelgradeNode = {
			left: customerArticleReferenceField,
			operator: 'OR',
			right: steelgradeDescriptionField,
			parenthesized: false
		};

		const globalNode = {
			left: supplierCustomerNode,
			operator: 'OR',
			right: articleSteelgradeNode,
			parenthesized: true
		};

		return luceneToString(globalNode);
	}

	async goToFilter() {
		if (!this.currentFilters) {
			return;
		}
		this.listFilterService.filterToEdit = {
			filterState: this.currentFilters,
			datacenterType: this.currentFilters.datacenterType
		};
		await this.router.navigate(
			['filter'],
			{ queryParams: { type: this.datacenterType }, relativeTo: this.route }
		);
	}

	checkCddWeekData(order: Order) {
		return this.orderService.checkCddWeekData(order.cddWeek);
	}

	private getAppliedFilters() {
		const listFilter: ListFilter = {
			filterState: [],
			datacenterType: this.datacenterType
		};
		const appliedFilter = this.listFilterService.getAppliedFilters(this.datacenterType)
			?? listFilter;
		this.listFilterService.setCurrentFilter(appliedFilter);
		return appliedFilter;
	}
}
